import { html, render } from 'lit-html'
import { register } from '@polleverywhere/hmr-custom-element'
import '@polleverywhere/icon-element'
import '@polleverywhere/pe-button'
import './style.scss'

const MAX_TITLE_LENGTH = 32
const MAX_CONTENT_LENGTH = 128

function handleClick (e) {
  const name = e.target.name || e.target.parentNode.name
  const event = new CustomEvent(`click:${name}`, {
    bubbles: true,
    detail: {
      // Be reserved here and carefully consider if consumers actually need any
      // more detail info before adding it.
    }
  })
  this.dispatchEvent(event)
}

function iconTemplate (type) {
  const iconTypeToName = {
    error: 'critical',
    info: 'info',
    success: 'success',
    warning: 'alert'
  }

  const name = iconTypeToName[type]
  if (!name) {
    throw new Error(`Invalid notification type '${type}'. Valid names are [${Object.keys(iconTypeToName).join(',')}]`)
  }

  return html`<icon-element type='glyph' name='${name}'></icon-element>`
}

function template (title, content, action, type) {
  const role = ['info', 'success'].includes(type) ? 'status' : 'alert'

  return html`<aside role='${role}' aria-label='${type}'>
    <div name='icon'>${iconTemplate(type)}</div>
    <div name='content'>
      <div name='title'>${title}</div>
      ${content ? html`<p>${content}</p>` : ''}
      <div name='controls'>
        <pe-button type=link @click=${handleClick}
          name='dismiss' label='Dismiss'></pe-button>
        ${action ? html`
        <pe-button type=link @click=${handleClick}
          name='action' label='${action}'></pe-button>` : ''}
      </div>
    </div>
  </aside>`
}

function renderTemplate () {
  if (!this.isConnected) { return }

  let title = this.getAttribute('title') || ''
  let content = this.getAttribute('content') || ''
  const action = this.getAttribute('action') || undefined
  const type = this.getAttribute('type') || undefined

  if (!title) {
    throw new Error('Title is required')
  }

  title = title.substring(0, MAX_TITLE_LENGTH)
  content = content.substring(0, MAX_CONTENT_LENGTH)

  render(template(title, content, action, type), this, {
    eventContext: this
  })
}

/**
 * NotificationCard represents a single card to display containing a
 * notification with a required type and title. Additional content is optional.
 * The card may contain a dismiss and/or an action CTA button, both which emits
 * events for consumers to listen to.
 *
 * @element notification-card
 *
 * @fires click:action - Fires when the action CTA button is clicked
 * @fires click:dismiss - Fires when the dismiss button is clicked
 *
 * @attr {string} action - If provided, adds a CTA with the given label that
 *  then emits a custom event when clicked.
 * @attr {string} content - If provided, adds a description below the title
 * @attr {string} title - Title is required
 * @attr {string} type - Type is required and is one of four values that
 *  specifies the color of the notification: [error,info,success,warning]
 *
 * @cssprop --background-color - Set background color
 * @cssprop --color - Set foreground color
 */
export default class NotificationCard extends HTMLElement {
  static get tagName () { return 'notification-card' }

  static get observedAttributes () {
    return [
      'action',
      'content',
      'title',
      'type'
    ]
  }

  connectedCallback () {
    renderTemplate.call(this)
  }

  attributeChangedCallback () {
    renderTemplate.call(this)
  }
}

register(module, NotificationCard.tagName, NotificationCard)
